import type { ViewNode } from "pincerjs";
import type { Engine } from "./engine";
import { byId, h } from "./dom";

/*
The plain way to draw any node, which is all `access` does. Nodes with children are headings (level by depth); a node
that opens and closes is a heading holding a button that says whether it's open, with its summary beside it while
closed; controls are labelled by their parent node's wording, and described by their problem and help text.
*/
export function generic(node: ViewNode, main: HTMLElement, engine: Engine) {
  const key = node.compoundKey;
  if (node.parentKey == null) {
    main.append(h("h1", { id: `${key}-label` }, node.wording));
  }
  else if (node.open != null) {
    main.append(heading(node, disclosure(node, engine)));
    if (node.open === false && node.summary != null) main.append(h("p", { class: "summary", id: `${key}-summary` }, node.summary));
    main.append(richView(node, engine));
  }
  else if (node.control != null) {
    main.append(control(node, engine));
  }
  else if (node.childKeys.length > 0) {
    main.append(heading(node, node.wording), richView(node, engine));
  }
  else {
    main.append(h("p", { id: `${key}-label` }, node.wording));
  }
}

// Headings go to h6; below that, the same role and level for assistive technology
export function heading(node: ViewNode, ...inner: (Node | string)[]): HTMLElement {
  const level = node.depth + 1;
  const id = `${node.compoundKey}-label`;
  if (level <= 6) return h(`h${level}`, { id }, ...inner);
  return h("div", { role: "heading", "aria-level": String(level), id }, ...inner);
}

// The button that opens or closes a node, through its opener (a checkbox in the tree)
export function disclosure(node: ViewNode, engine: Engine, content: (Node | string)[] = [node.wording]): HTMLElement {
  const button = h("button", {
    class: "toggle",
    type: "button",
    "aria-expanded": node.open ? "true" : "false",
    "aria-controls": `${node.compoundKey}-children`,
    "aria-describedby": node.open === false && node.summary != null ? `${node.compoundKey}-summary` : null,
  }, ...content);
  button.addEventListener("click", () => engine.perform(`${node.compoundKey}-open`, node.open ? "false" : "true"));
  return button;
}

/*
A control, ready to go in the page: a button, a form with a text box (or text area) and Save, a select or a
checkbox. Its element has the id KEY-control (a button is its own control).
*/
export interface ControlOptions {
  // For a text box that goes with a button beside it (type a message, then Send): Enter does both, there's no
  // Save of its own, and leaving the box saves what's in it, so pressing the button sends what was typed
  then?: ViewNode;
}

export function control(node: ViewNode, engine: Engine, options: ControlOptions = {}): HTMLElement {
  const key = node.compoundKey;
  let result: HTMLElement;
  switch (node.control) {
    case "Button": {
      result = h("button", { type: "button", id: `${key}-control` }, node.wording);
      result.addEventListener("click", () => engine.perform(key, null));
      return result;
    }
    case "Input":
    case "Multiline": {
      const field = node.control === "Multiline"
        ? h("textarea", { id: `${key}-control`, rows: "4" })
        : h("input", { id: `${key}-control`, type: inputType(node) });
      const value = () => (field as HTMLInputElement | HTMLTextAreaElement).value;
      const then = options.then;
      const form = h("form", { class: "field" }, field, then === undefined ? h("button", { type: "submit" }, "Save") : null);
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        engine.perform(key, value());
        // Sent: the box is ready for the next one (the app empties its copy too)
        if (then !== undefined) { engine.perform(then.compoundKey, null); (field as HTMLInputElement).value = ""; }
      });
      if (then !== undefined) field.addEventListener("change", () => engine.perform(key, value()));
      result = form;
      break;
    }
    case "Select": {
      const select = h("select", { id: `${key}-control` }, ...(node.choices ?? []).map((choice) => h("option", { value: choice.value }, choice.wording)));
      select.addEventListener("change", () => engine.perform(key, (select as HTMLSelectElement).value));
      result = select;
      break;
    }
    case "Checkbox": {
      const checkbox = h("input", { id: `${key}-control`, type: "checkbox" });
      checkbox.addEventListener("change", () => engine.perform(key, String((checkbox as HTMLInputElement).checked)));
      result = checkbox;
      break;
    }
    default:
      return h("p", {}, node.wording);
  }
  const holder = h("div", { class: "control" }, result);
  updateControl(node, holder);
  return holder;
}

// Brings a control's state up to date without replacing it
export function updateControl(node: ViewNode, main: HTMLElement) {
  const control = main.querySelector<HTMLElement>(`[id="${node.compoundKey}-control"]`);
  if (control === null) return;
  if (node.control === "Button") {
    control.textContent = node.wording;
    return;
  }
  control.setAttribute("aria-invalid", node.showAs === "Problem" ? "true" : "false");
  if (node.control === "Checkbox") {
    (control as HTMLInputElement).checked = node.wording === "true";
  }
  else if (document.activeElement !== control) {
    // Leave alone whatever someone is typing
    (control as HTMLInputElement).value = node.wording;
  }
}

/*
What names and describes a control: its parent's wording (a button names itself), and the problem and help text
beside it.
*/
export function describe(node: ViewNode, main: HTMLElement, engine: Engine) {
  const control = main.querySelector<HTMLElement>(`[id="${node.compoundKey}-control"]`);
  const parent = engine.node(node.parentKey);
  if (control === null || parent === undefined) return;
  if (node.control !== "Button") {
    const label = byId(`${parent.compoundKey}-label`);
    if (label !== null) control.setAttribute("aria-labelledby", label.id);
    else control.setAttribute("aria-label", parent.wording);
  }
  const described = ["problem", "helpText"]
    .map((key) => engine.child(parent, key))
    .filter((sibling) => sibling !== undefined)
    .map((sibling) => `${sibling!.compoundKey}-label`);
  if (described.length > 0) control.setAttribute("aria-describedby", described.join(" "));
  else control.removeAttribute("aria-describedby");
}

function inputType(node: ViewNode): string {
  if (node.rich?.type !== "DateTimePicker") return "text";
  return ({ DATE: "date", TIME: "time", DATETIME: "datetime-local" } as Record<string, string>)[node.rich.format ?? ""] ?? "text";
}

/*
Rich views every renderer gets. The availability grid is a table (dates across, times down) with a button in each
cell; the same suggestions are listed as text below it, as for every rich node.
*/
export function richView(node: ViewNode, engine: Engine): Node | string {
  const rich = node.rich;
  if (rich?.type !== "AvailabilityGrid" || rich.slots == null) return "";
  const dates: string[] = [], times: string[] = [];
  const cells = new Map<string, (typeof rich.slots)[number]>();
  for (const slot of rich.slots) {
    const date = slot.start.substring(0, 10), time = slot.start.substring(11, 16);
    if (!dates.includes(date)) dates.push(date);
    if (!times.includes(time)) times.push(time);
    cells.set(`${date}T${time}`, slot);
  }
  dates.sort(); times.sort();
  const table = h("table", { class: "grid" },
    h("caption", {}, `Times in ${rich.zone ?? ""}`),
    h("tr", {}, h("td", {}), ...dates.map((date) => h("th", { scope: "col" }, date))),
  );
  for (const time of times) {
    const row = h("tr", {}, h("th", { scope: "row" }, time));
    for (const date of dates) {
      const slot = cells.get(`${date}T${time}`);
      if (slot === undefined) { row.append(h("td", {})); continue; }
      const choose = h("button", { type: "button" }, `${slot.available} can, ${slot.undecided} not decided, ${slot.minutes} minutes`);
      choose.addEventListener("click", () => engine.perform(`${slot.childKey}-confirmCandidate`, null));
      row.append(h("td", {}, choose));
    }
    table.append(row);
  }
  return table;
}
